// frontend/src/pages/driver/DriverCOD.jsx
import { useState, useEffect } from 'react';
import axios from 'axios';

const API = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';

const DriverCOD = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [dateFilter, setDateFilter] = useState('today');

  useEffect(() => {
    fetchCODSummary();
  }, [dateFilter]);

  const getDateRange = () => {
    const now = new Date();
    let startDate = new Date();
    let endDate = new Date();

    switch (dateFilter) {
      case 'today':
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        break;
      case 'yesterday':
        startDate.setDate(now.getDate() - 1);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(startDate);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'week':
        startDate.setDate(now.getDate() - 7);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        break;
      case 'month':
        startDate.setDate(now.getDate() - 30);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
        break;
      default:
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date();
    }

    return {
      date_from: startDate.toISOString().split('T')[0],
      date_to: endDate.toISOString().split('T')[0]
    };
  };

  const getDateLabel = () => {
    const labels = {
      today: 'Өнөөдөр',
      yesterday: 'Өчигдөр',
      week: '7 хоног',
      month: '30 хоног'
    };
    return labels[dateFilter] || 'Өнөөдөр';
  };

  const fetchCODSummary = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const dateRange = getDateRange();
      
      const response = await axios.get(`${API}/driver/cod-summary`, {
        headers: { Authorization: `Bearer ${token}` },
        params: dateRange
      });
      
      if (response.data.success) {
        setData(response.data.data);
      }
    } catch (err) {
      console.error('COD summary error:', err);
      setError(err.response?.data?.message || 'Мэдээлэл татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${month}/${day}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Уншиж байна...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          <p className="font-medium">⚠️ Алдаа</p>
          <p className="text-sm">{error}</p>
          <button 
            onClick={fetchCODSummary}
            className="mt-3 px-4 py-2 bg-red-100 rounded-lg text-sm hover:bg-red-200"
          >
            Дахин оролдох
          </button>
        </div>
      </div>
    );
  }

  const { summary, daily, balance } = data || {};

  // Тооцоолол
  const totalAmount = summary?.total_cod || 0; // Нийт хүргэлтийн дүн
  const collectedAmount = summary?.total_collected || 0; // Жолооч авсан COD дүн
  const collectedDeliveryFee = summary?.collected_delivery_fee || 0; // Жолооч авсан хүргэлтийн төлбөр
  const owedAmount = balance?.owed_to_company || 0; // Төлөх дүн

  return (
    <div className="p-4 space-y-4">
      {/* Header with Filter */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
        <div className="flex items-center justify-between">
          <h1 className="text-lg font-bold text-gray-800">💰 COD тооцоо</h1>
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
          >
            <option value="today">📅 Өнөөдөр</option>
            <option value="yesterday">⏪ Өчигдөр</option>
            <option value="week">📆 7 хоног</option>
            <option value="month">🗓️ 30 хоног</option>
          </select>
        </div>
        <p className="text-sm text-gray-500 mt-2">📅 {getDateLabel()}</p>
      </div>

      {/* Summary Cards */}
      <div className="space-y-3">
        {/* Нийт дүн Card */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-4 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-200 text-xs mb-1">📦 Нийт хүргэлтийн дүн</p>
              <p className="font-bold text-2xl">{totalAmount.toLocaleString()}₮</p>
            </div>
            <div className="text-right">
              <div className="bg-white/20 px-3 py-2 rounded-xl">
                <p className="font-bold text-xl">{summary?.delivery_count || 0}</p>
                <p className="text-blue-200 text-xs">хүргэлт</p>
              </div>
            </div>
          </div>
          <p className="text-blue-200 text-xs mt-2 pt-2 border-t border-white/20">📅 {getDateLabel()}</p>
        </div>

         {/* Цуглуулсан & Хүргэлтийн төлбөр & Төлөх дүн */}
        <div className="grid grid-cols-3 gap-3">
          {/* COD Цуглуулсан дүн */}
          <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-4 text-white shadow-lg">
            <h3 className="text-green-100 text-xs font-medium mb-2">💵 COD авсан</h3>
            <p className="font-bold text-xl">{collectedAmount.toLocaleString()}₮</p>
            <p className="text-green-200 text-xs mt-1">Барааны төлбөр</p>
          </div>

          {/* Хүргэлтийн төлбөр авсан */}
          <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-4 text-white shadow-lg">
            <h3 className="text-blue-100 text-xs font-medium mb-2">🚚 Хүргэлт авсан</h3>
            <p className="font-bold text-xl">{collectedDeliveryFee.toLocaleString()}₮</p>
            <p className="text-blue-200 text-xs mt-1">Захиалагчаас</p>
          </div>

          {/* Төлөх дүн */}
          <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-2xl p-4 text-white shadow-lg">
            <h3 className="text-orange-100 text-xs font-medium mb-2">🏦 Төлөх</h3>
            <p className="font-bold text-xl">{owedAmount.toLocaleString()}₮</p>
            <p className="text-orange-200 text-xs mt-1">Компанид</p>
          </div>
        </div>
      </div>

      {/* Balance Info */}
      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
        <h3 className="font-semibold text-gray-800 mb-3">📊 Тооцооны мэдээлэл</h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-gray-600">Нийт цуглуулсан (бүх цаг)</span>
            <span className="font-semibold text-gray-800">{(balance?.total_collected || 0).toLocaleString()}₮</span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <span className="text-gray-600">Шилжүүлсэн</span>
            <span className="font-semibold text-green-600">{(balance?.total_transferred || 0).toLocaleString()}₮</span>
          </div>
          <div className="flex justify-between items-center py-2">
            <span className="font-semibold text-gray-800">Үлдэгдэл</span>
            <span className="font-bold text-xl text-orange-600">{owedAmount.toLocaleString()}₮</span>
          </div>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <span className="text-2xl">ℹ️</span>
          <div>
            <p className="font-medium text-blue-800">Барааны төлбөр (COD)</p>
            <p className="text-sm text-blue-600 mt-1">
              Төлөх дүн = Цуглуулсан - Шилжүүлсэн. 
              Компанид шилжүүлэх үлдэгдлээ тооцоолохдоо ашиглана уу.
            </p>
          </div>
        </div>
      </div>

      {/* Daily Breakdown */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
        <div className="p-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-800">📅 Өдөр бүрийн задаргаа</h3>
        </div>

        {daily && daily.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {daily.map((day, idx) => (
              <div key={idx} className="p-4 flex items-center justify-between hover:bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center">
                    <span className="text-sm font-bold text-gray-600">{formatDate(day.date)}</span>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">
                      {new Date(day.date).toLocaleDateString('mn-MN', { 
                        weekday: 'short',
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </p>
                    <p className="text-sm text-gray-500">{day.delivery_count} хүргэлт</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-gray-800">{(parseFloat(day.amount) || 0).toLocaleString()}₮</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <span className="text-5xl mb-4 block">📭</span>
            <p className="text-gray-500">Энэ хугацаанд COD тооцоо байхгүй</p>
          </div>
        )}
      </div>

      {/* Chart (Simple Bar) */}
      {daily && daily.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
          <h3 className="font-semibold text-gray-800 mb-4">📊 График</h3>
          
          <div className="space-y-3">
            {daily.slice(0, 7).reverse().map((day, idx) => {
              const maxAmount = Math.max(...daily.map(d => parseFloat(d.amount) || 0));
              const percentage = maxAmount > 0 ? ((parseFloat(day.amount) || 0) / maxAmount) * 100 : 0;
              
              return (
                <div key={idx} className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 w-10">{formatDate(day.date)}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-400 to-green-500 rounded-full flex items-center justify-end pr-2"
                      style={{ width: `${Math.max(percentage, 5)}%` }}
                    >
                      {percentage > 30 && (
                        <span className="text-xs text-white font-medium">
                          {(parseFloat(day.amount) || 0).toLocaleString()}₮
                        </span>
                      )}
                    </div>
                  </div>
                  {percentage <= 30 && (
                    <span className="text-xs text-gray-600 font-medium">
                      {(parseFloat(day.amount) || 0).toLocaleString()}₮
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default DriverCOD;